/**
 * VO Product Options Price Enhancement
 * Adds dynamic pricing to option values that have data-price attributes
 */
(function() {
    'use strict';
    
    let processingTimeout = null;
    let isProcessing = false;
    let observer = null;
    
    /**
     * Gets product variants from JSON script tag
     */
    function getProductVariants() {
        // Try hidden product-variant-selector (works when original selector exists)
        const hiddenSelector = document.querySelector('product-variant-selector[hidden]');
        if (hiddenSelector) {
            const variantsJSON = hiddenSelector.querySelector('[data-variants-json]');
            if (variantsJSON) {
                try {
                    const variants = JSON.parse(variantsJSON.textContent);
                    if (Array.isArray(variants)) {
                        return variants;
                    }
                } catch (e) {
                    // Fall through to fallback
                }
            }
        }
        
        // Try script tag (fallback for VO Product Options pages)
        const variantsScript = document.querySelector('script[data-variants-json]');
        if (variantsScript) {
            try {
                const variants = JSON.parse(variantsScript.textContent);
                if (Array.isArray(variants)) {
                    return variants;
                }
            } catch (e) {
                console.error('Error parsing variants JSON:', e);
            }
        }
        
        return null;
    }
    
    /**
     * Finds the cheapest variant price to use as base price
     */
    function getBasePrice(variants) {
        if (!Array.isArray(variants) || variants.length === 0) {
            return null;
        }
        
        const prices = variants
            .map(v => v?.price)
            .filter(p => typeof p === 'number' && !isNaN(p) && p > 0);
        
        if (prices.length === 0) {
            return null;
        }
        
        return Math.min(...prices);
    }
    
    /**
     * Adds data-price attributes to first selector options based on variant prices
     */
    function enhanceFirstSelectorOptions() {
        const firstSelector = document.querySelector('select[name="name0"]');
        if (!firstSelector) {
            return;
        }
        
        const variants = getProductVariants();
        if (!Array.isArray(variants) || variants.length === 0) {
            return;
        }
        
        const basePrice = getBasePrice(variants);
        if (typeof basePrice !== 'number' || basePrice <= 0) {
            return;
        }
        
        // Create a map of variant ID to variant object for quick lookup
        const variantMap = new Map(
            variants
                .filter(v => v?.id != null)
                .map(v => [String(v.id), v])
        );
        
        // Find only unprocessed options with variant-id attributes
        const options = firstSelector.querySelectorAll('option[variant-id]:not([data-price-processed])');
        
        options.forEach(option => {
            const variantId = option.getAttribute('variant-id');
            if (!variantId) return;
            
            const variant = variantMap.get(variantId);
            if (!variant || typeof variant.price !== 'number') {
                option.setAttribute('data-price-processed', 'true');
                return;
            }
            
            const additionalCost = variant.price - basePrice;
            
            if (additionalCost > 0) {
                // Convert cents to pounds and format to 2 decimal places
                const additionalCostPounds = (additionalCost / 100).toFixed(2);
                option.setAttribute('data-price', additionalCostPounds);
            }
            
            // Mark as processed to avoid re-processing
            option.setAttribute('data-price-processed', 'true');
        });
    }
    
    function enhanceOptionValues() {
        const selectors = document.querySelectorAll('.selector-wrapper select, .bcpo-simple select, .bcpo-default select');
        
        selectors.forEach(select => {
            if (!select || select.tagName !== 'SELECT') return;
            
            const optionsWithPrice = select.querySelectorAll('option[data-price]:not([data-enhanced])');
            
            optionsWithPrice.forEach(option => {
                const price = option.getAttribute('data-price');
                if (!price) return;
                
                const originalText = option.textContent?.trim() || '';
                if (!originalText) return;
                
                const numPrice = parseFloat(price) || 0;
                const formattedPrice = numPrice.toLocaleString('en-GB', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                });
                option.textContent = `${originalText} + £${formattedPrice}`;
                option.setAttribute('data-enhanced', 'true');
                option.setAttribute('data-original-value', option.value);
            });
        });
    }
    
    function restoreOriginalValues() {
        document.querySelectorAll('option[data-enhanced]').forEach(option => {
            const originalValue = option.getAttribute('data-original-value');
            if (originalValue) option.value = originalValue;
        });
    }

    function clearNonMainSelectors(bcpoElem) {
        // Find and reset all non-main selector displays
        const selectorWrappers = bcpoElem.querySelectorAll('.selector-wrapper:not(:first-child)');
        selectorWrappers.forEach(wrapper => {
            const select = wrapper.querySelector('select');

            if (select && select.name !== 'name0') {
                // Reset the select element
                select.selectedIndex = 0;

                // Override the display elements
                const displayTexts = wrapper.querySelectorAll('.bcpo-value, .bcpo-addon');
                displayTexts.forEach(displayText => {
                    if (displayText.classList.contains('bcpo-value')) {
                        displayText.textContent = 'Choose one';
                    } else if (displayText.classList.contains('bcpo-addon')) {
                        displayText.remove(); // Remove price addon
                    }
                });
            }
        });
    }

    // Export function for external use - needed because it's called from s_product.liquid
    window.clearNonMainSelectors = clearNonMainSelectors;

    // Debounced enhancement runner - prevents rapid multiple executions
    function scheduleEnhancements(delay = 200) {
        clearTimeout(processingTimeout);
        processingTimeout = setTimeout(runEnhancements, delay);
    }
    
    // Runs both enhancements in sequence with processing guard

    function runEnhancements() {
        if (isProcessing) {
            return;
        }
        
        isProcessing = true;
        
        try {
            enhanceFirstSelectorOptions();
            enhanceOptionValues();
        } catch (error) {
            console.error('Error enhancing product options:', error);
        } finally {
            isProcessing = false;
        }
    }
    
    //Sets up mutation observer with filtering for relevant changes only

    function setupObserver() {
        if (observer) {
            observer.disconnect();
        }
        
        observer = new MutationObserver(mutations => {
            // Only respond to relevant DOM changes
            const hasRelevantChange = mutations.some(mutation => {
                // Check for new nodes matching our selectors
                if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                    return [...mutation.addedNodes].some(node => {
                        if (node.nodeType !== 1) return false;
                        return (
                            node.matches?.('.selector-wrapper, .bcpo-simple, .bcpo-default, select[name="name0"]') ||
                            node.querySelector?.('.selector-wrapper, .bcpo-simple, .bcpo-default, select[name="name0"]')
                        );
                    });
                }
                
                // Check for variant-id attribute changes on option elements
                if (mutation.type === 'attributes' && mutation.attributeName === 'variant-id') {
                    return mutation.target.tagName === 'OPTION';
                }
                
                return false;
            });
            
            if (hasRelevantChange) {
                scheduleEnhancements();
            }
        });
        
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['variant-id']
        });
    }
    
    function initialize() {
        // Initial enhancement with delay to ensure VO Product Options is loaded
        scheduleEnhancements(500);
        
        // Setup observer for dynamic content
        setupObserver();
        
        // Restore values before form submission
        document.querySelectorAll('form[action*="/cart"]').forEach(form => {
            form.addEventListener('submit', () => {
                setTimeout(restoreOriginalValues, 50);
            });
        });
        
        // Catch late-loading content
        setTimeout(() => runEnhancements(), 2000);
    }
    
    // Initialize
    document.readyState === 'loading' 
        ? document.addEventListener('DOMContentLoaded', initialize)
        : initialize();
        
})();